import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import api from 'api/api';
import { ReleaseRef } from 'hooks/data/versions/types';
import { useResolvedRelease } from 'hooks/useVersionInfo';

import { FetchDatasetParams } from '../types';

import { DirectContributionResponse, UseDirectContributionParams } from './types';

const fetchDirectContibution = async (params: FetchDatasetParams & Pick<UseDirectContributionParams, 'indicator_id'>) => {
  const { data } = await api.get(`/spold/direct_contributions`, { params });

  return data;
};

/**
 * `enabled` lets callers defer the request (e.g. until the row is on screen); defaults to fetching.
 * `release` targets a specific release instead of the route's — the indicator id is release-scoped, so
 * it must always travel with the release it came from.
 *
 * Pass a negative `indicator_id` to mean "not known yet"; 0 is a valid id, so it must not be treated
 * as absent (this mirrors the `dataset_id !== -1` convention in useLciaScores).
 */
const useDirectContribution = (
  { dataset_id, indicator_id }: UseDirectContributionParams,
  enabled = true,
  release?: ReleaseRef
) => {
  const { version, system_model } = useResolvedRelease(release);
  return useQuery<DirectContributionResponse, AxiosError>({
    queryKey: ['directContribution', dataset_id, indicator_id, version, system_model],
    queryFn: () => fetchDirectContibution({ version, system_model, dataset_id, indicator_id }),
    enabled: enabled && !!dataset_id && indicator_id >= 0,
  });
};

export default useDirectContribution;
